import { ReactNode, useCallback } from "react";
import { Section, MenuItem } from "react-aria-components";

import DropdownMenuItem, { DropdownMenuItemProps } from "ds/components/DropdownMenu/Item";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import { External } from "components/icons/generated";
import { MainNavigationSecondaryMenuItemKeys } from "components/MainNavigation/config/menuItemsSecondary";

import { type MainNavigationMenuItem } from "../../../types";
import useMainNavigationStore from "../../../stores/useMainNavigationStore";
import useMainNavigationActiveStates from "../../../hooks/useActiveStates";
import MainNavigationCounter from "../Counter";
import MainNavigationBetaIndicator from "../BetaIndicator";
import MainNavigationPinAction from "../PinAction";
import styles from "./styles.module.css";

type MainNavigationDropdownMenuItemProps = Omit<DropdownMenuItemProps, "children"> & {
  item: MainNavigationMenuItem;
  children?: ReactNode;
  onCloseDropdown?: () => void;
};

const MainNavigationDropdownMenuItem = ({
  item,
  children,
  onCloseDropdown,
}: MainNavigationDropdownMenuItemProps) => {
  const itemCounts = useMainNavigationStore((state) => state.itemCounts);
  const { activeMenuItemId } = useMainNavigationActiveStates();

  const isActive = item.id === activeMenuItemId;
  const itemCount = itemCounts[item.id] || 0;

  const handleNavigation = useCallback(() => {
    item.onAction?.();

    if (onCloseDropdown) {
      onCloseDropdown();
    }
  }, [item, onCloseDropdown]);

  const isDanger = item.id === MainNavigationSecondaryMenuItemKeys.Logout;

  return (
    <Section className={styles.innerSection}>
      <DropdownMenuItem
        href={item.to || item.href}
        target={item.href ? "_blank" : undefined}
        onAction={handleNavigation}
        active={isActive}
        className={styles.menuItem}
        danger={isDanger}
      >
        <Box align="center" gap="medium">
          {children || item.title}
          {item.href && <Icon src={External} size="default" noShrink />}
        </Box>

        {item.beta ? (
          <MainNavigationBetaIndicator className={styles.counter} />
        ) : (
          <MainNavigationCounter count={itemCount} inversed={isActive} className={styles.counter} />
        )}
      </DropdownMenuItem>

      <MenuItem className={styles.pinItem}>
        <MainNavigationPinAction item={item} />
      </MenuItem>
    </Section>
  );
};

export default MainNavigationDropdownMenuItem;
